import Vue from 'vue'
import Router from 'vue-router'
import About from './views/About.vue'
import Library from './views/theme/Library.vue'
import Dashboard from './views/theme/Dashboard.vue'
import Groups from './views/theme/Groups.vue'
import Group from './views/theme/Group.vue'
import Signup from './views/auth/Signup.vue'
import Signin from './views/auth/Signin.vue'
import SelectOpe from './views/auth/SelectOpe.vue'
import Profile from './views/profile/Profile.vue'
import Lock from './views/profile/Lock.vue'
import { getToken, verifyToken } from '@/api/login'
import { hasOpeAccess, getOperationBuyerState } from '@/api/operations'
import Compte from './views/profile/Compte.vue'
import Foyer from './views/profile/Foyer.vue'
import Appartment from './views/profile/Appartment.vue'
import AppartmentView from './views/AppartmentView.vue'
import AppartmentRecapView from './views/AppartmentRecapView.vue'
import IndexProfil from './views/profile/Index.vue'
import IndexTheme from './views/theme/IndexTheme.vue'
import ResetPassword from './views/auth/ResetPassword.vue'
import AlertForm from '@/views/auth/AlertForm.vue'
import { UserModule } from './store/modules/user'
import FoyerFunnel from '@/views/funnel/FoyerFunnel.vue'
import IndexFunnel from '@/views/funnel/IndexFunnel.vue'

Vue.use(Router)

function tokenValid() {
    const token = getToken()
    if (token === null) {
        return false
    }

    const tokenDecoded = verifyToken(token)
    const timestamp = Date.now()
    const timestampSeconds = Math.floor(timestamp / 1000)
    if (tokenDecoded.data.exp <= timestampSeconds) {
        return false
    }

    return true
}

async function guardRequiredInfos(to, from, next): Promise<boolean> {
    await UserModule.Init()

    if (
        UserModule.foyerCompleted === null ||
        UserModule.appartCompleted === null
    ) {
        return true
    }

    if (
        UserModule.foyerCompleted === true &&
        UserModule.appartCompleted === true
    ) {
        // redirect to dashboard
        if (to.name === 'funnel-appart' || to.name === 'funnel-foyer') {
            next({ name: 'home' })
            return false
        }
        return true
    }

    if (UserModule.foyerCompleted === false) {
        // redirect to foyer
        if (to.name !== 'funnel-foyer') {
            next({ name: 'funnel-foyer' })
            return false
        }
    }

    if (
        UserModule.foyerCompleted === true &&
        UserModule.appartCompleted === false
    ) {
        // redirect to appart
        if (to.name !== 'funnel-appart') {
            next({ name: 'funnel-appart' })
            return false
        }
    }

    return true
}

async function guardSelectOpe(to, from, next) {
    const opeAccess = await hasOpeAccess()
    if (!tokenValid() || !opeAccess) {
        if (tokenValid() && !opeAccess) {
            next({ name: 'select-ope' })
        } else {
            next({ name: 'signin' })
        }
    } else {
        if (await guardRequiredInfos(to, from, next)) {
            next()
        }
    }
}

function guardSignin(to, from, next) {
    if (!tokenValid()) {
        next({ name: 'signin' })
    } else {
        next()
    }
}

function guardReset(to, from, next) {
    if (getToken() === null && to.query.token !== undefined) {
        next()
    } else {
        next({ name: 'signin' })
    }
}

function guardConnected(to, from, next) {
    if (tokenValid()) {
        next({ name: 'home' })
    } else {
        next()
    }
}

const router = new Router({
    mode: 'history',
    base: process.env.BASE_URL,
    routes: [
        {
            path: '/',
            component: IndexTheme,
            beforeEnter: guardSelectOpe,
            children: [
                {
                    path: '',
                    name: 'home',
                    component: Dashboard,
                    beforeEnter: guardSelectOpe,
                    meta: {
                        title: 'Accueil',
                    },
                },
                /** Profile **/
                {
                    path: '/profil',
                    name: 'index-profil',
                    component: IndexProfil,
                    redirect: 'profile/mon-profil',
                    beforeEnter: guardSelectOpe,
                    children: [
                        {
                            path: 'mon-profil',
                            name: 'profile',
                            component: Profile,
                            redirect: 'mon-profil',
                            beforeEnter: guardSelectOpe,
                            children: [
                                {
                                    path: '',
                                    name: 'foyer',
                                    component: Foyer,
                                    beforeEnter: guardSelectOpe,
                                    meta: {
                                        title: 'Mon foyer',
                                    },
                                },
                            ],
                        },
                        {
                            path: 'mon-coffre-fort',
                            name: 'lock',
                            component: Lock,
                            beforeEnter: guardSelectOpe,
                            meta: {
                                title: 'Mon coffre-fort',
                            },
                        },
                        {
                            path: 'mon-compte',
                            name: 'compte',
                            component: Compte,
                            beforeEnter: guardSelectOpe,
                            meta: {
                                title: 'Mon compte',
                            },
                        },
                    ],
                },

                /** Theme **/
                {
                    path: '/groupes',
                    name: 'groups',
                    component: Groups,
                    beforeEnter: guardSelectOpe,
                    meta: {
                        title: 'Mes groupes',
                    },
                },
                {
                    path: '/groupes/:id',
                    name: 'group',
                    component: Group,
                    beforeEnter: guardSelectOpe,
                },
                {
                    path: 'mon-appart',
                    name: 'appart',
                    component: AppartmentView,
                    beforeEnter: async (to, from, next) => {
                        await guardSelectOpe(to, from, async (arg) => {
                            if (arg == null) {
                                const opeBuyerState = await getOperationBuyerState()
                                let phase = 0
                                if (opeBuyerState.phase != null) {
                                    phase = opeBuyerState.phase
                                }
                                const status = opeBuyerState.status
                                const shouldRedirectToRecap = (opeBuyerState.planValidated) || (phase > 3 && !!status && status !== 'pending' && status !== 'no-operation')
                                if (shouldRedirectToRecap) {
                                    next({ name: 'appart-recap' })
                                } else {
                                    next()
                                }
                            } else {
                                next(arg)
                            }
                        })
                    },
                    meta: {
                        title: 'Mon appartement',
                    },
                },
                {
                    path: 'mon-appart-recap',
                    name: 'appart-recap',
                    component: AppartmentRecapView,
                    beforeEnter: async (to, from, next) => {
                        await guardSelectOpe(to, from, async (arg) => {
                            if (arg == null) {
                                const opeBuyerState = await getOperationBuyerState()
                                if (!opeBuyerState.planValidated && (opeBuyerState.phase < 4 || opeBuyerState.status === 'pending')) {
                                    next({ name: 'appart' })
                                } else {
                                    next()
                                }
                            } else {
                                next(arg)
                            }
                        })
                    },
                    meta: {
                        title: 'Mon appartement récapitulatif',
                    },
                },
            ],
        },

        /** Funnel **/
        {
            path: '/',
            name: 'funnel-index',
            component: IndexFunnel,
            beforeEnter: guardSelectOpe,
            children: [
                {
                    path: '/foyer',
                    name: 'funnel-foyer',
                    component: FoyerFunnel,
                    beforeEnter: guardSelectOpe,
                    meta: {
                        title: 'Mon foyer',
                    },
                },
                {
                    path: '/appartement',
                    name: 'funnel-appart',
                    component: Appartment,
                    beforeEnter: guardSelectOpe,
                    meta: {
                        title: 'Mon appartement',
                    },
                },
            ],
        },
        /** Auth **/
        {
            path: '/inscription',
            name: 'signup',
            component: Signup,
            beforeEnter: guardConnected,
            meta: {
                title: 'Inscription',
            },
        },
        {
            path: '/alerte',
            name: 'alert',
            component: AlertForm,
            meta: {
                title: 'Me tenir au courant',
            },
        },
        {
            path: '/connexion',
            name: 'signin',
            component: Signin,
            beforeEnter: guardConnected,
            meta: {
                title: 'Connexion',
            },
        },
        {
            path: '/selection-operation',
            name: 'select-ope',
            component: SelectOpe,
            beforeEnter: guardSignin,
            meta: {
                title: "Sélection d'une opération",
            },
        },
        {
            path: '/reinitialisation-motdepasse',
            name: 'reset-password',
            component: ResetPassword,
            beforeEnter: guardReset,
            meta: {
                title: 'Réinitialisation du mot de passe',
            },
        },
        /** Library Components **/
        {
            path: '/bibliotheque',
            name: 'library',
            component: Library,
            beforeEnter: guardSignin,
        },

        /** Annexes **/
        {
            path: '/a-propos',
            name: 'about',
            component: About,
        },
        {
            path: '/conditions-ventes',
            name: 'conditions-ventes',
            component: Dashboard,
        },
    ],
})

router.beforeEach((to, from, next) => {
    if (to.meta.title) {
        document.title = 'Yvivre - ' + to.meta.title
    } else {
        document.title = 'Yvivre'
    }
    next()
})

export default router
